import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
  albumApi,
  type Album,
  type AlbumGenerateRequest,
} from '../api/album-service';
import { toast } from '@/shared/lib/toast';
import { createLogger } from '@/shared/lib/logger';
import { ApiClientError } from '@/shared/lib/api';
import { useLimitErrorPaywall } from '@/shared/hooks';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { useJobQueueStore } from '@/features/design/job-queue';
import { AnalyticsEvent, trackEvent } from '@/shared/lib/analytics';

const logger = createLogger('useAlbum');

interface UseAlbumOptions {
  conceptId: string;
}

export function useAlbum({ conceptId }: UseAlbumOptions) {
  const queryClient = useQueryClient();
  const t = useTranslations('album');
  const tCommon = useTranslations('common');
  // Server-driven paywall — handles both 422 typed-limit errors (paywall on
  // body) and 403 SUBSCRIPTION_REQUIRED (cached `paywall_payloads` on the
  // effective plan). Mirrors RenderSettingsSheet on Apple and surfaceUnhandled
  // on Android.
  const routeLimitErrorToPaywall = useLimitErrorPaywall();

  // Query to list existing albums
  const albumsQuery = useQuery({
    queryKey: cacheKeys.albums.list(conceptId),
    queryFn: () => albumApi.list(conceptId),
    enabled: !!conceptId,
    staleTime: 5 * 60 * 1000, // 5 minutes - cache albums across concept switches
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Mutation to generate album. No lockId — album id is server-assigned
  // and doesn't exist before the response.
  const generateMutation = useMutation({
    mutationKey: ['albums', 'generate'],
    mutationFn: (data: AlbumGenerateRequest) => {
      logger.info('Generating album:', {
        conceptId,
        request: data,
      });
      // Funnel: user-triggered album generation (pairs with album_generated
      // fired from the realtime completion handler). Cross-platform contract.
      trackEvent(AnalyticsEvent.ALBUM_REQUESTED);
      return albumApi.generate(conceptId, data);
    },
    onSuccess: async (data, variables) => {
      logger.info('Album generation API response:', {
        conceptId,
        albumId: data.id,
        status: data.status,
        isCached: data.status === 'completed',
        duplicateExists: data.duplicate_exists,
      });

      if (data.duplicate_exists) {
        // Duplicate exists — don't process further, let UI handle via onDuplicateExists callback
        logger.info('Duplicate album exists, deferring to UI');
        return;
      }

      if (data.status === 'completed' && data.download_url) {
        // Cached result - WebSocket will show toast via ALBUM_COMPLETED event
        logger.info('Cached album returned, invalidating queries');
        queryClient.invalidateQueries({ queryKey: cacheKeys.albums.list(conceptId) });
      } else {
        // New generation — add pending album job for instant UI feedback
        logger.info('New generation started, adding pending album job');

        const store = useJobQueueStore.getState();
        const now = new Date().toISOString();

        store.addPendingAlbumJob({
          id: data.id,
          jobType: 'album',
          status: data.status || 'pending',
          startedAt: null,
          durationPredicted: 120, // Default album duration
          attemptNumber: 1,
          createdAt: now,
          updatedAt: now,
          completedAt: null,
          conceptId,
          projectId: '',
          conceptName: null,
          conceptSequenceNumber: 0,
          projectName: '',
          conceptAngleId: null,
          angleType: null,
          albumTemplate: variables.template ?? 'standard',
          albumIncludeBlueprints: variables.include_blueprints ?? false,
          albumFormat: 'pdf',
          albumLanguage: '',
          albumFilename: null,
          albumPublicUrl: null,
        });
      }
      // New generation - WebSocket will handle all notifications (ALBUM_CREATED → ALBUM_COMPLETED)
      // JobQueue Store is the single source of truth for album generation status
    },
    onError: (error: unknown) => {
      // Typed paywall errors (subscription_required, *_limit_exceeded) route
      // through the root-mounted LimitPaywallDialog with server-owned copy.
      if (error instanceof ApiClientError && routeLimitErrorToPaywall(error)) {
        return;
      }

      let message = 'Failed to generate album';
      if (error instanceof ApiClientError) {
        // Extract detailed message from JSON:API error format
        message = error.getFirstError();
      } else if (error instanceof Error) {
        message = error.message;
      }

      console.error('[useAlbum] Album generation failed:', {
        conceptId,
        error: message,
      });
      toast.error(message);
    },
  });

  // Delete album with undo support
  // Deletes on server immediately (soft-delete), undo calls restore API.
  const deleteAlbumWithUndo = useCallback(
    async (albumId: string) => {
      const albums = albumsQuery.data || [];
      const album = albums.find((a) => a.id === albumId);
      if (!album) return;

      // Optimistic update: remove album from query cache
      queryClient.setQueryData<Album[]>(cacheKeys.albums.list(conceptId), (old) =>
        old ? old.filter((a) => a.id !== albumId) : []
      );

      // Remove from job queue store
      const store = useJobQueueStore.getState();
      const updatedQueue = store.jobQueue.filter((job) => job.id !== albumId);
      store.replaceJobQueue(updatedQueue);

      try {
        // Delete on server immediately (soft-delete)
        await albumApi.delete(conceptId, albumId);
        logger.info('Album soft-deleted on server:', { albumId });

        // Show toast with undo action
        toast.undoable(t('albumDeleted'), {
          action: {
            label: tCommon('undo'),
            onClick: async () => {
              // Restore album in query cache
              queryClient.setQueryData<Album[]>(
                cacheKeys.albums.list(conceptId),
                (old) => {
                  const current = old || [];
                  if (current.some((a) => a.id === albumId)) return current;
                  return [...current, album].sort(
                    (a, b) =>
                      new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime()
                  );
                }
              );

              // Restore on server
              try {
                await albumApi.restore(conceptId, albumId);
                logger.info('Album restored on server:', { albumId });
                queryClient.invalidateQueries({
                  queryKey: cacheKeys.albums.list(conceptId),
                });
              } catch (restoreError) {
                logger.error('Failed to restore album:', {
                  albumId,
                  error: restoreError,
                });
                toast.error(t('errors.albumRestoreFailed'));
                queryClient.invalidateQueries({
                  queryKey: cacheKeys.albums.list(conceptId),
                });
              }
            },
          },
        });
      } catch (error) {
        logger.error('Failed to delete album:', { albumId, error });
        // Restore on failure
        queryClient.setQueryData<Album[]>(cacheKeys.albums.list(conceptId), (old) => {
          const current = old || [];
          if (current.some((a) => a.id === albumId)) return current;
          return [...current, album].sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          );
        });
        toast.error(t('errors.albumGenerationFailed'));
      }
    },
    [albumsQuery.data, conceptId, queryClient, t, tCommon]
  );

  // Function to open album in new window
  // Opens album in a new browser tab where user can view and download it
  const downloadAlbum = useCallback(
    (albumId?: string) => {
      const albums = albumsQuery.data || [];
      const album = albums.find((a) => a.id === albumId);

      if (!album?.public_url) {
        toast.error(t('errors.albumNotAvailable'));
        return;
      }

      // Open album in new window/tab
      window.open(album.public_url, '_blank');

      toast.success(t('albumOpenedInNewWindow'));
    },
    [albumsQuery.data]
  );

  return {
    // State
    albums: albumsQuery.data || [],
    isLoadingAlbums: albumsQuery.isLoading,

    // Actions
    generateAlbum: generateMutation.mutate,
    isGenerating: generateMutation.isPending, // Only track mutation state, JobQueue Store tracks actual job status
    lastGenerateResult: generateMutation.data, // Last generation result — check duplicate_exists
    resetGenerateResult: generateMutation.reset,

    deleteAlbum: deleteAlbumWithUndo,
    isDeleting: false, // Optimistic delete — album disappears immediately

    downloadAlbum,
  };
}
